import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import { getSupabaseAuthHeaders } from "@/lib/supabaseAuth";

interface SubscriptionState {
  subscribed: boolean;
  planTier: string | null;
  subscriptionEnd: string | null;
  loading: boolean;
}

// Features available per tier (cumulative - higher tiers include lower tier features)
const TIER_FEATURES: Record<string, string[]> = {
  free: [],
  starter: ['recording'],
  professional: ['recording', 'breakout_rooms'],
  business: ['recording', 'breakout_rooms', 'analytics'],
};

export function useSubscription() {
  const { user } = useAuth();
  const [state, setState] = useState<SubscriptionState>({
    subscribed: false,
    planTier: null,
    subscriptionEnd: null,
    loading: true,
  });

  const fetchSubscription = useCallback(async () => {
    if (!user) {
      setState({
        subscribed: false,
        planTier: null,
        subscriptionEnd: null,
        loading: false,
      });
      return;
    }

    try {
      const authHeaders = await getSupabaseAuthHeaders();
      if (!authHeaders) {
        setState({
          subscribed: false,
          planTier: null,
          subscriptionEnd: null,
          loading: false,
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('check-subscription', {
        headers: authHeaders,
      });
      if (error) throw error;
      
      setState({
        subscribed: data?.subscribed ?? false,
        planTier: data?.plan_tier ?? null,
        subscriptionEnd: data?.subscription_end ?? null,
        loading: false,
      });
    } catch (error) {
      console.error('Error fetching subscription:', error);
      setState(prev => ({ ...prev, loading: false }));
    }
  }, [user]);

  useEffect(() => {
    fetchSubscription();
  }, [fetchSubscription]);

  const getTier = useCallback((): string => {
    if (!state.subscribed || !state.planTier) return 'free';
    return state.planTier;
  }, [state.subscribed, state.planTier]);

  const hasFeature = useCallback((feature: string): boolean => {
    return true; // All features open for all users
  }, []);

  const canUseRecording = useCallback(() => true, []);
  const canUseBreakoutRooms = useCallback(() => true, []);
  const canUseAnalytics = useCallback(() => true, []);

  return {
    ...state,
    tier: getTier(),
    hasFeature,
    canUseRecording,
    canUseBreakoutRooms,
    canUseAnalytics,
    refetch: fetchSubscription,
  };
}
